import { FC, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import API_URLS from '../../../../../constants/api-urls';
import { world } from '../../../../../languages';
import { AVAILABLE_WORLDS } from '../../../../../languages/constants';
import qualityEvents from '../../../../../services/quality-events/quality-events';
import isMobileAppWithDeeplinkRedirect from '../../../../../utils/is-mobile-app';
import { useAppSelector } from '../../../../../store';
import emailHash from '../../../../../utils/email-hash';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import { PAYMENT_METHOD_KEYS } from '../../../payment.constants';
import {
    addFailedPaymentMethod,
    addPaymentError,
    changeState,
    resetErrors,
    serializeForm,
    validateForm
} from '../../../reducers';
import { getMobilePathParams } from '../../kushki/utils/kushki';
import { localesMap } from '../paypal-ppcp-controls/constants';
import { getUserOS, getUserSID } from '../../../../../services/quality-events/quality-events.helpers';
import './amazon-pay-btn.scss';

type AmazonPayBtnProps = {};

let scriptLoaded = false;

const AmazonPayBtn: FC<AmazonPayBtnProps> = () => {
    const { paymentOptionsData, paymentCart } = useAppSelector((state) => state.payment);
    const user = useAppSelector((state) => state.main.user);
    const currency = useAppSelector((state) => state.main.currency);
    const dispatch = useDispatch();

    const unescapeJsonString = (jsonString: string) => {
        return jsonString.replace(/\\+/g, '');
    };

    const getLang = () => {
        return {
            ...localesMap,
            [AVAILABLE_WORLDS.GR]: 'en_GB'
        }[world];
    };

    const getCurrencyCode = () => {
        const { useWorldCurrency } = serializeQueryParams();

        if (useWorldCurrency) {
            return currency.code;
        }

        return 'EUR';
    };

    const renderButton = () => {
        try {
            // @ts-ignore
            const amazonPayButton = window.amazon.Pay.renderButton('#AmazonPayButton', {
                buttonColor: 'Gold',
                checkoutLanguage: getLang(),
                ledgerCurrency: getCurrencyCode(),
                merchantId: paymentOptionsData?.amazonPay.merchantId,
                placement: 'Checkout',
                productType: 'PayOnly',
                publicKeyId: paymentOptionsData?.amazonPay.publicKey
            });

            amazonPayButton.onClick(async () => {
                dispatch(resetErrors());

                const params = serializeQueryParams();
                const isInvoiceFormValid = dispatch(validateForm());
                const formData = dispatch(serializeForm());
                const requestConfig: { [key: string]: string | boolean } = {};

                if (!isInvoiceFormValid) {
                    return;
                }

                if (params.materialId) {
                    requestConfig.materialId = params.materialId as string;
                }

                // @ts-ignore
                emailHash.storeSHA256(formData.email);

                qualityEvents.checkoutPage.startPayment(user.id, PAYMENT_METHOD_KEYS.AMAZON_PAY, paymentCart?.finalPrice);

                try {
                    dispatch(changeState({ isDataProcessing: true }));

                    const mobileParams = getMobilePathParams(true) as Object;
                    const response = await axios.post(
                        `${API_URLS.PAYMENT_ATTEMPT}/AmazonPay`,
                        {
                            acceptAgb: 'on',
                            currencyCode: 'EUR',
                            deepLinkRedirect: isMobileAppWithDeeplinkRedirect(),
                            invoiceRequested: false,
                            sid: getUserSID(),
                            ...(getUserOS() && { os: getUserOS() }),
                            ...mobileParams,
                            ...formData
                        },
                        { params: { ...requestConfig } }
                    );

                    amazonPayButton.initCheckout({
                        createCheckoutSessionConfig: {
                            algorithm: 'AMZN-PAY-RSASSA-PSS',
                            payloadJSON: unescapeJsonString(response.payload),
                            publicKeyId: paymentOptionsData?.amazonPay.publicKey,
                            signature: response.signature
                        }
                    });
                } catch (e) {
                    const error = e as { response?: { data?: { message?: string; reason?: string } } };

                    dispatch(changeState({ isDataProcessing: false }));
                    dispatch(addPaymentError({}));
                    qualityEvents.checkoutPage.failedPayment(
                        user.id,
                        PAYMENT_METHOD_KEYS.AMAZON_PAY,
                        paymentCart?.finalPrice,
                        error?.response?.data?.message || error?.response?.data?.reason
                    );
                }

                window.scrollTo(0, 0);
            });
        } catch (err) {
            dispatch(addFailedPaymentMethod(PAYMENT_METHOD_KEYS.AMAZON_PAY, (err as Error)?.message));
            console.info(`Something went wrong during initialization ${PAYMENT_METHOD_KEYS.AMAZON_PAY}`, err);
        }
    };

    const addScript = (url: string) => {
        if (scriptLoaded) {
            renderButton();
        } else {
            const script = document.createElement('script');

            script.onload = renderButton;
            script.src = url;

            document.head.appendChild(script);
            scriptLoaded = true;
        }
    };

    useEffect(() => {
        addScript('https://static-eu.payments-amazon.com/checkout.js');
    }, []);

    return (
        <div className='amazon-btn-wrapper'>
            <div id='AmazonPayButton' />
        </div>
    );
};

export default AmazonPayBtn;
